import { OrderDetail as ApiOrderDetail } from "randmar-api-client";
import React, { useMemo } from "react";
import { FormattedNumber } from "react-intl";
import { Link } from "react-router-dom";
import { Card } from "randmarcomps";
import CurrencyUtils from "../../components/util/CurrencyUtils";
import { useManufacturer } from "../../contexts/WithManufacturer";
import Path from "../../routes/Path";
import { OrderDetail as LegacyOrderDetail } from "../../stores/document/Document";

interface IProps {
  openOrders?: (LegacyOrderDetail | ApiOrderDetail)[];
  navLink?: string;
}

type NormalizedOrderDetail = {
  documentType?: string | null;
  warehouseCode?: string | null;
  extendedPrice?: number | undefined;
}

const isLegacyOrderDetail = (order: LegacyOrderDetail | ApiOrderDetail): order is LegacyOrderDetail => "documentType" in order;

const normalizeOrderDetail = (order: LegacyOrderDetail | ApiOrderDetail): NormalizedOrderDetail => {
  if (isLegacyOrderDetail(order)) {
    return {
      documentType: order.documentType,
      warehouseCode: order.warehouseCode,
      extendedPrice: order.extendedPrice
    };
  }
  return {
    documentType: order.DocumentType,
    warehouseCode: order.WarehouseCode,
    extendedPrice: order.ExtendedPrice
  };
};

const OpenOrdersSummary: React.FC<IProps> = ({ openOrders, navLink }) => {
  const { applicationId } = useManufacturer();

  const normalizedOpenOrders = useMemo(() => {
    return openOrders?.map(normalizeOrderDetail) || [];
  }, [openOrders]);

  const orders = normalizedOpenOrders.filter(o => o.documentType === "Order");
  const shipments = normalizedOpenOrders.filter(o => o.documentType === "Shipment");

  const currentOrder = orders.filter(o => o.warehouseCode !== "3PLE");
  const currentOrderCount = currentOrder.length;
  const currentOrderAmount = currentOrder.map(o => o.extendedPrice || 0).reduce((prev, next) => prev + next, 0);

  const delayedOrder = orders.filter(o => o.warehouseCode === "3PLE");
  const delayedOrderCount = delayedOrder.length;
  const delayedOrderAmount = delayedOrder.map(o => o.extendedPrice || 0).reduce((prev, next) => prev + next, 0);

  const shipmentCount = shipments!.length;
  const shipmentAmount = shipments.map(s => s.extendedPrice || 0).reduce((prev, next) => prev + next, 0);

  return (
    <Card className="w-full overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
      <div className="flex flex-col md:flex-row">
        <Link
          to={navLink || Path.orderDetails(applicationId)}
          className="flex-1 bg-blue-50 p-6 text-center transition-colors hover:bg-blue-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ops-focus-ring)]"
        >
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-600">Processing</p>
          <div className="mt-2 flex items-center justify-center gap-4">
            <p className="text-3xl font-semibold text-slate-900">{currentOrderCount}</p>
            <div className="h-8 border-l border-slate-200" aria-hidden="true" />
            <p className="text-3xl font-semibold text-slate-900">
              <FormattedNumber value={currentOrderAmount} {...CurrencyUtils.format} />
            </p>
          </div>
        </Link>
        <div className="border-b border-gray-100 md:border-b-0 md:border-l" />
        <Link
          to={navLink || Path.orderDetails(applicationId)}
          className="flex-1 bg-emerald-50 p-6 text-center transition-colors hover:bg-emerald-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ops-focus-ring)]"
        >
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-600">Shipped</p>
          <div className="mt-2 flex items-center justify-center gap-4">
            <p className="text-3xl font-semibold text-slate-900">{shipmentCount}</p>
            <div className="h-8 border-l border-slate-200" aria-hidden="true" />
            <p className="text-3xl font-semibold text-slate-900">
              <FormattedNumber value={shipmentAmount} {...CurrencyUtils.format} />
            </p>
          </div>
        </Link>
        <div className="border-b border-gray-100 md:border-b-0 md:border-l" />
        <Link
          to={navLink || Path.orderDetails(applicationId)}
          className="flex-1 bg-amber-50 p-6 text-center transition-colors hover:bg-amber-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ops-focus-ring)]"
        >
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-600">Delayed</p>
          <div className="mt-2 flex items-center justify-center gap-4">
            <p className="text-3xl font-semibold text-slate-900">{delayedOrderCount}</p>
            <div className="h-8 border-l border-slate-200" aria-hidden="true" />
            <p className="text-3xl font-semibold text-slate-900">
              <FormattedNumber value={delayedOrderAmount} {...CurrencyUtils.format} />
            </p>
          </div>
        </Link>
      </div>
    </Card>
  );
};

export default OpenOrdersSummary;
